/**
 * OkendoSchemaIntegrator
 * Fetches review data and updates the JSON-LD Product schema.
 */
class OkendoSchemaIntegrator {
  static DEFAULT_REVIEW_LIMIT = 25;

  constructor() {
    this.storeId = null;
    this.locale = window.Shopify.locale || null;
    this.reviewLimit = null;
    this.cacheKeyPrefix = "okendo_cache_";

    // Cache duration in milliseconds (e.g., 5 minutes)
    this.cacheDuration = 5 * 60 * 1000;
  }

  /**
   * Initialized review settings from the okendo widget settings.
   */
  initializedReviewSettings() {
    const script = document.getElementById("oke-reviews-settings");
    if (!script) return null;

    try {
      const reviewSettings = JSON.parse(script.textContent);

      // Store ID
      if (reviewSettings.subscriberId) {
        this.storeId = reviewSettings.subscriberId;
      }

      // Widget Locale
      if (reviewSettings.locale && reviewSettings.matchCustomerLocale === false) {
        this.locale = reviewSettings.locale;
      }

      // Widget Review Limit
      if (
        reviewSettings.widgetSettings?.reviewsWidget?.reviews?.list
          ?.initialPageSize
      ) {
        this.reviewLimit = this.getReviewLimit(
          reviewSettings.widgetSettings.reviewsWidget.reviews.list?.initialPageSize
        );
      }

      return true;
    } catch (e) {
      console.error("Failed to parse oke-reviews-settings JSON:", e);
      return false;
    }
  }

  init() {
    this.initializedReviewSettings();

    if (!this.storeId) return;

    this.cleanupCacheIfSettingsChanged();
    this.updateSchema("reviews-pdp-container");
  }

  getReviewLimit(limit) {
    const defaultLimit = OkendoSchemaIntegrator.DEFAULT_REVIEW_LIMIT;
    let effectiveLimit = parseInt(limit, 10);

    if (!Number.isFinite(effectiveLimit) || effectiveLimit <= 0) {
      effectiveLimit = defaultLimit;
    } else if (effectiveLimit > defaultLimit) {
      effectiveLimit = defaultLimit;
    }

    return effectiveLimit;
  }

  /**
   * Cleans up all sessionStorage cache entries with cacheKeyPrefix if Review Settings changes.
   */
  cleanupCacheIfSettingsChanged() {
    const STORE_ID_KEY = "okendo_last_store_settings";
    const lastStoreSettings = localStorage.getItem(STORE_ID_KEY);
    const currentSettings = `${this.storeId}_${this.reviewLimit}`;

    if (lastStoreSettings && lastStoreSettings !== currentSettings) {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.cacheKeyPrefix)) {
          console.log(`Removed: `, key);
          sessionStorage.removeItem(key);
        }
      }
    }

    // Update last used settings
    localStorage.setItem(STORE_ID_KEY, currentSettings);
  }

  /**
   * Main execution method
   * @param {string} selector - The element containing the data-group-id
   */
  async updateSchema(selector) {
    const element = document.querySelector(selector);
    const groupId = element?.dataset?.okeReviewsGroupId;

    if (!groupId) {
      console.warn("OkendoSchemaIntegrator: No Group ID found on element.");
      return;
    }

    try {
      const reviewData = await this.getReviewData(groupId);
      if (reviewData) {
        this.injectIntoSchema(reviewData);
      }
    } catch (error) {
      console.error("OkendoSchemaIntegrator Error:", error);
    }
  }

  /**
   * Fetches data with a simple sessionStorage cache to handle identical repeated calls
   */
  async getReviewData(groupId) {
    const cacheKey = `${this.cacheKeyPrefix}${this.locale}_${groupId}`;
    const cached = sessionStorage.getItem(cacheKey);

    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < this.cacheDuration) {
        return data;
      }
    }

    const baseUrl = `https://api.okendo.io/v1/stores/${this.storeId}/collections/${groupId}/reviews`;
    let params = [];

    const limit = this.reviewLimit;
    if (limit && limit > 0) {
      params.push(`limit=${limit}`);
    }

    if (this.locale) {
      params.push(`locale=${this.locale}`);
    }

    let url = baseUrl;
    if (params.length > 0) {
      url += `?${params.join("&")}`;
    }

    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);

    const result = await response.json();

    // Cache the result
    sessionStorage.setItem(
      cacheKey,
      JSON.stringify({
        data: result,
        timestamp: Date.now(),
      })
    );

    return result;
  }

  /**
   * Locates the Product Schema and updates it with review data
   */
  injectIntoSchema(reviewData) {
    if (!reviewData.reviews) return;

    const schemaScript = document.querySelector(
      'script[type="application/ld+json"][data-schema="product"]'
    );

    if (!schemaScript) return;

    try {
      const cleanSchemaScriptString = schemaScript.textContent
        .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
        .trim();

      let schema = JSON.parse(cleanSchemaScriptString);

      // Target the ProductGroup or Product directly if it's the main type, otherwise look for Product within the graph
      const target =
        schema["@type"] === "Product" || schema["@type"] === "ProductGroup"
          ? schema
          : null;

      if (target && reviewData.reviews) {
        // Map individual reviews to the schema format
        target.review = reviewData.reviews
          .map((review) => ({
            "@type": "Review",
            author: {
              "@type": "Person",
              name: review.reviewer?.displayName || "Anonymous",
            },
            datePublished: review.dateCreated.split("T")[0],
            reviewBody: review.body,
            name: review.title || "Product Review",
            reviewRating: {
              "@type": "Rating",
              ratingValue: review.rating.toString(),
              bestRating: "5",
              worstRating: "1",
            },
          }));

        schemaScript.textContent = JSON.stringify(schema);
      }
    } catch (e) {
      console.error("Error updating ProductGroup schema:", e);
    }
  }
}

// Initialization
new OkendoSchemaIntegrator().init();
